import { useCallback, useState } from "react";

import * as api from "../api/client";
import { ConfidenceBadge } from "../components/ConfidenceBadge";
import { PromptForm } from "../components/PromptForm";
import { SortableTh } from "../components/SortableTh";
import { useApi } from "../hooks/useApi";
import { useSortableRows } from "../hooks/useSortableRows";
import type { CreatePromptPayload, Prompt, UpdatePromptPayload } from "../types/api";

type PromptSortKey = "name" | "type" | "status" | "alpha" | "usageCount" | "satisfaction";
const ACCESSORS: Record<PromptSortKey, (p: Prompt) => string | number> = {
  name: (p) => p.name,
  type: (p) => p.type,
  status: (p) => p.status,
  alpha: (p) => p.alpha,
  usageCount: (p) => p.usageCount,
  satisfaction: (p) => {
    const rated = p.likes + p.dislikes;
    return rated > 0 ? p.likes / rated : 0;
  },
};

export function PromptsPage() {
  const { data: prompts, loading, error, refetch } = useApi(() => api.listPrompts(), []);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Prompt | null>(null);
  // Default: most-used prompt first — useful without clicking (PK 2026-07-25 directive).
  const { sorted, sort, toggleSort } = useSortableRows(prompts, ACCESSORS, {
    key: "usageCount",
    direction: "desc",
  });

  const handleCreate = useCallback(
    async (data: CreatePromptPayload) => {
      await api.createPrompt(data);
      setShowForm(false);
      refetch();
    },
    [refetch]
  );

  const handleUpdate = useCallback(
    async (data: CreatePromptPayload) => {
      if (!editing) return;
      const payload: UpdatePromptPayload = {
        name: data.name,
        description: data.description,
        systemPrompt: data.systemPrompt,
        userPromptTemplate: data.userPromptTemplate,
        thinkingInstructions: data.thinkingInstructions,
        minTrafficShare: data.minTrafficShare,
      };
      await api.updatePrompt(editing.id, payload);
      setEditing(null);
      refetch();
    },
    [editing, refetch]
  );

  const handleDisable = useCallback(
    async (id: string) => {
      await api.disablePrompt(id);
      refetch();
    },
    [refetch]
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-500 text-sm">Loading prompts...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4" role="alert">
        <div className="text-red-800 text-sm font-medium">Error: {error}</div>
      </div>
    );
  }

  if (showForm) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 mb-1">Create Prompt</h2>
          <p className="text-sm text-slate-600">Add a new prompt configuration to the system</p>
        </div>
        <PromptForm onSubmit={handleCreate} onCancel={() => setShowForm(false)} />
      </div>
    );
  }

  if (editing) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 mb-1">Edit Prompt: {editing.name}</h2>
          <p className="text-sm text-slate-600">Modify the prompt configuration</p>
        </div>
        <PromptForm initial={editing} onSubmit={handleUpdate} onCancel={() => setEditing(null)} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 mb-1">Prompts</h2>
          <p className="text-sm text-slate-600">Manage AI prompt configurations and templates</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          type="button"
          className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
        >
          Create Prompt
        </button>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <SortableTh label="Name" sortKey="name" sort={sort} onSort={toggleSort} sticky />
                <SortableTh label="Type" sortKey="type" sort={sort} onSort={toggleSort} />
                <SortableTh label="Status" sortKey="status" sort={sort} onSort={toggleSort} />
                <SortableTh label="Alpha/Beta" sortKey="alpha" sort={sort} onSort={toggleSort} />
                <SortableTh label="Usage" sortKey="usageCount" sort={sort} onSort={toggleSort} />
                <SortableTh
                  label="Satisfaction"
                  sortKey="satisfaction"
                  sort={sort}
                  onSort={toggleSort}
                />
                <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Confidence
                </th>
                <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {sorted.map((p) => {
                const rated = p.likes + p.dislikes;
                const satisfaction = rated > 0 ? p.likes / rated : 0;
                const confidence = rated >= 100 ? "high" : "low";
                return (
                  <tr key={p.id} className="group hover:bg-slate-50 transition-colors">
                    <td className="sticky left-0 z-10 bg-white group-hover:bg-slate-50 px-6 py-4 text-sm font-medium text-slate-900">
                      {p.name}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600">{p.type}</td>
                    <td className="px-6 py-4 text-sm text-slate-600">{p.status}</td>
                    <td className="px-6 py-4 text-sm text-slate-600">
                      {p.alpha}/{p.beta}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-900">{p.usageCount}</td>
                    <td className="px-6 py-4 text-sm text-slate-900">
                      {(satisfaction * 100).toFixed(1)}%
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <ConfidenceBadge badge={confidence} />
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <div className="flex gap-2">
                        <button
                          onClick={() => setEditing(p)}
                          type="button"
                          className="px-3 py-1.5 bg-slate-100 text-slate-700 text-sm font-medium rounded hover:bg-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-400 transition-colors"
                        >
                          Edit
                        </button>
                        {p.status === "active" && (
                          <button
                            onClick={() => handleDisable(p.id)}
                            type="button"
                            className="px-3 py-1.5 bg-red-50 text-red-700 text-sm font-medium rounded hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-400 transition-colors"
                          >
                            Disable
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
